import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { ProdClient } from 'client/client';
import Location from 'components/pages/ContactUs/Location';
import { CountriesWithOffices } from 'components/pages/ContactUs/types';
import { getTranslations } from 'helpers/translations';
import _compact from 'lodash/compact';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import _toPairs from 'lodash/toPairs';
import _uniq from 'lodash/uniq';
import cacheData from 'memory-cache';
import {
  Contact as ContactModel,
  ContentPage as ContentPageModel,
  Office as OfficeModel,
  OfficePageContent,
} from 'models';
import { GetStaticPaths } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { usePaginatedPeople } from 'store/paginated-people';
import { getContactUsVariants } from 'utils/overviewVariants';
import { peopleCall } from 'utils/peopleCall';

import { CountriesWithOfficesCollection } from '../../ContactUs/types';
export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: 'blocking',
    paths: [],
  };
};

const limit = 1000;
let skip = 0;
let namesData = [] as ContactModel[];
let filterData = [] as ContactModel[];

async function fillNamesData(peopleCallFn: typeof peopleCall) {
  try {
    const response = await peopleCallFn(0)
      .limitParameter(limit)
      .skipParameter(skip)
      .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
      .toPromise();
    if (
      !response.data.pagination.nextPage &&
      response.data?.pagination?.totalCount != null &&
      response.data?.pagination?.totalCount < skip
    ) {
      skip = 0;
      return;
    }

    namesData = namesData.concat(...response.data.items);
    skip += limit;
    await fillNamesData(peopleCallFn);
  } catch (e) {
    console.warn('error', e);
  }
}
async function fillFilterData(languageCodename: string) {
  try {
    const response = await ProdClient.items<ContactModel>()
      .type('contact')
      .languageParameter(languageCodename)
      .includeTotalCountParameter()
      .elementsParameter(['department', 'city', 'market'])
      .limitParameter(limit)
      .skipParameter(skip)
      .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
      .toPromise();
    if (!response.data.pagination.nextPage) {
      skip = 0;
      return;
    }
    filterData = filterData.concat(...response.data.items);
    skip += limit;
    await fillFilterData(languageCodename);
  } catch (e) {
    console.log('error', e);
  }
}

export const getStaticCountryProps = async ({ urlParam, locale }: { locale: string; urlParam: string }) => {
  const languageCodename = locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  cacheData.put('languageCodename', languageCodename);
  await fillNamesData(peopleCall);
  await fillFilterData(languageCodename);

  const translations = await getTranslations(locale);

  const [
    {
      data: { items: contentPageModel },
    },
    {
      data: { items: officesModel },
    },
  ] = await Promise.all([
    ProdClient.items<ContentPageModel>()
      .type('content_page')
      .containsFilter('elements.category', ['office'])
      .equalsFilter('elements.url', urlParam)
      .languageParameter(languageCodename)
      .limitParameter(1)
      .depthParameter(3)
      .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
      .toPromise(),
    ProdClient.items<OfficeModel>()
      .type('office')
      .anyFilter('elements.country', [urlParam])
      .orderByAscending('elements.isHeadOffice')
      .languageParameter(languageCodename)
      .limitParameter(3)
      .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
      .toPromise(),
  ]);

  const variants = await getContactUsVariants();
  const names = namesData.map((item) => item.elements.displayName.value);
  const allCountries = _sortBy(
    contentPageModel.map(({ elements, system }) => {
      if ((elements.content.linkedItems[0] as OfficePageContent).elements.isMainVariant.value[0]?.codename === 'yes')
        return {
          title: elements.urlSlugGenerator.value,
          region: translations.location(elements.summaryOfficePageGroup?.value[0]?.codename),
          url: elements.url.value,
          codename: system.codename,
        };
    }),
    'title',
  ).filter((page) => page) as CountriesWithOffices[];
  return {
    countriesWithOffices: {
      all: allCountries,
      grouped: _sortBy(_toPairs(_groupBy(allCountries, 'region')), '0'),
    },
    contentPageModel: contentPageModel[0] ?? null,
    variants,
    names,
    offices: officesModel.map(({ elements, system }) => ({
      address: createRichTextHtmlResolver(nodeParser).resolveRichText({
        element: elements.address,
      }).html,
      city: elements.city.value,
      country: elements.country.value[0].name,
      cvr: elements.taxNumber.value,
      description: createRichTextHtmlResolver(nodeParser).resolveRichText({
        element: elements.info,
      }).html,
      email: elements.email.value,
      id: system.id,
      isMain: !!elements.isHeadOffice.value[0]?.name,
      officeName: elements.officeName.value || null,
      phone: elements.phoneNumber.value,
    })),
  };
};

type OfficesListProps = {
  countriesWithOffices: CountriesWithOfficesCollection;
};
type LocationPageProps = Awaited<ReturnType<typeof getStaticCountryProps>> & OfficesListProps;

const LocationPage = ({ countriesWithOffices, names, ...props }: LocationPageProps) => {
  const { setNames } = usePaginatedPeople();
  const router = useRouter();
  const pathParts = router.asPath.split('/').filter(Boolean);
  const lastSegment = pathParts[pathParts.length - 1];
  useEffect(() => {
    setNames(names);
  }, []);

  return <Location {...props} lastSegment={lastSegment} countriesWithOffices={countriesWithOffices} />;
};

export default LocationPage;
